/**
 * Componente CalendarView
 * Vista de calendario semanal o mensual para citas y eventos.
 * Navegación: por semana en vista semana, por mes en vista mes.
 * Siempre muestra botón "Hoy" para ir al día actual.
 * @module components/appointments/CalendarView
 */

import React, { useMemo, useState } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/react/24/outline';
import type { CalendarEvent } from '@/types/calendar.types';
import { Button } from '@/components/ui/button';
import {
  getAvatarColor,
  getEventAvatarInitials,
  getEventTypeLabel,
  getGridLabel,
  getGridTooltip,
  getEventDisplayName,
} from '@/utils/calendarAvatars';
import { getModalityIcon } from '@/utils/appointmentHelpers';

const WEEK_STARTS_ON = 1; // Lunes

export type CalendarViewMode = 'week' | 'month' | 'day';

interface CalendarViewProps {
  events: CalendarEvent[];
  /** Fecha de referencia del periodo mostrado (inicio de semana o de mes según viewMode) */
  periodStart: Date;
  /** Vista actual: semana o mes */
  viewMode: CalendarViewMode;
  /** Cambiar periodo: delta &gt; 0 siguiente, delta &lt; 0 anterior (semana o mes según viewMode) */
  onNavigate: (delta: number) => void;
  /** Ir al periodo que contiene hoy */
  onGoToToday: () => void;
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  className?: string;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  periodStart,
  viewMode,
  onNavigate,
  onGoToToday,
  onEventClick,
  onDateClick,
  className = '',
}) => {
  const [zoomed, setZoomed] = useState(false);

  const { daysToShow, title } = useMemo(() => {
    if (viewMode === 'day') {
      return {
        intervalStart: periodStart,
        intervalEnd: periodStart,
        daysToShow: [periodStart],
        title: format(periodStart, "EEEE, d 'de' MMMM yyyy", { locale: es }),
      };
    }
    if (viewMode === 'week') {
      const start = startOfWeek(periodStart, { weekStartsOn: WEEK_STARTS_ON });
      const end = endOfWeek(periodStart, { weekStartsOn: WEEK_STARTS_ON });
      const days = eachDayOfInterval({ start, end });
      const title =
        days.length === 7
          ? `${format(days[0], 'd MMM', { locale: es })} – ${format(days[6], 'd MMM yyyy', { locale: es })}`
          : format(periodStart, 'd MMM yyyy', { locale: es });
      return { intervalStart: start, intervalEnd: end, daysToShow: days, title };
    }
    // Grid del mes: desde el lunes de la semana que contiene el día 1
    // hasta el domingo de la semana que contiene el último día del mes.
    // Esto asegura que cada día se alinee con su columna de día de la semana.
    const monthStart = startOfMonth(periodStart);
    const monthEnd = endOfMonth(periodStart);
    const start = startOfWeek(monthStart, { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(monthEnd, { weekStartsOn: WEEK_STARTS_ON });
    const days = eachDayOfInterval({ start, end });
    return {
      intervalStart: start,
      intervalEnd: end,
      daysToShow: days,
      title: format(periodStart, 'MMMM yyyy', { locale: es }),
    };
  }, [periodStart, viewMode]);

  // Agrupar eventos por fecha
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};
    events.forEach((event) => {
      const dateKey = format(new Date(event.date), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  }, [events]);

  const getEventsForDay = (date: Date): CalendarEvent[] => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return eventsByDate[dateKey] || [];
  };

  const getEventColor = (event: CalendarEvent) => {
    switch (event.type) {
      case 'appointment':
        return 'bg-blue-500';
      case 'call':
        return event.callDirection === 'inbound' ? 'bg-teal-500' : 'bg-green-600';
      case 'task':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm ${className}`}>
      {/* Header: título + Hoy (siempre visible) + anterior / siguiente */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <div className="flex items-center gap-2">
          {/* Zoom toggle — solo visible en vistas mes/semana */}
          {viewMode !== 'day' && (
            <Button
              onClick={() => setZoomed(z => !z)}
              variant="outline"
              size="sm"
              aria-label={zoomed ? 'Reducir celdas' : 'Ampliar celdas'}
              title={zoomed ? 'Reducir celdas' : 'Ampliar celdas'}
              className={`h-10 w-10 p-0 transition-colors ${zoomed ? 'bg-blue-100 border-blue-400 text-blue-700' : ''}`}
            >
              {zoomed
                ? <MagnifyingGlassMinusIcon className="h-5 w-5" />
                : <MagnifyingGlassPlusIcon className="h-5 w-5" />
              }
            </Button>
          )}
          <Button onClick={onGoToToday} variant="outline" size="sm">
            Hoy
          </Button>
          <Button
            onClick={() => onNavigate(-1)}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label={viewMode === 'day' ? 'Día anterior' : viewMode === 'week' ? 'Semana anterior' : 'Mes anterior'}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => onNavigate(1)}
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label={viewMode === 'day' ? 'Día siguiente' : viewMode === 'week' ? 'Semana siguiente' : 'Mes siguiente'}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="p-4">
        {viewMode === 'day' ? (
          /* ── Vista Diaria ── */
          (() => {
            const dayEvents = getEventsForDay(periodStart);
            const isTodayDate = isToday(periodStart);
            return (
              <div>
                {isTodayDate && (
                  <div className="mb-3 text-sm font-medium text-blue-600">Hoy</div>
                )}
                {dayEvents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No hay eventos para este día
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dayEvents.map((event) => {
                      const eventDate = new Date(event.date);
                      const timeStr = format(eventDate, 'HH:mm');
                      return (
                        <div
                          key={event.id}
                          onClick={() => onEventClick?.(event)}
                          className={`
                            flex items-start gap-3 p-3 rounded-lg border cursor-pointer
                            transition-colors hover:shadow-sm
                            ${isTodayDate ? 'border-blue-200 hover:bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}
                          `}
                        >
                          {/* Color indicator + hora */}
                          <div className="flex flex-col items-center gap-1 flex-shrink-0 w-14">
                            <div className={`w-3 h-3 rounded-full ${getEventColor(event)}`} />
                            <span className="text-xs text-gray-500">{timeStr}</span>
                          </div>
                          {/* Contenido */}
                          <div className="flex-1 min-w-0">
                            {/* Línea 1: Badge de tipo + nombre del cliente + icono modalidad (citas) */}
                            <div className="flex items-center gap-2">
                              <span className={`
                                text-[10px] px-1.5 py-0.5 rounded font-medium text-white flex-shrink-0
                                ${getEventColor(event)}
                              `}>
                                {getEventTypeLabel(event.type, event.callDirection)}
                              </span>
                              {event.type === 'appointment' && event.metadata?.modality && (
                                <span className="flex-shrink-0 text-gray-500" title={event.metadata.modality === 'in_person' ? 'Presencial' : 'Videollamada'}>
                                  {(() => {
                                    const ModalityIcon = getModalityIcon(event.metadata.modality);
                                    return <ModalityIcon className="w-4 h-4" aria-hidden />;
                                  })()}
                                </span>
                              )}
                              <span className="font-semibold text-sm text-gray-900 truncate">
                                {getEventDisplayName(event)}
                              </span>
                            </div>
                            {/* Línea 2: Si es tarea con contacto, mostrar el texto de la tarea debajo */}
                            {event.type === 'task' && event.contactName && (
                              <p className="text-xs text-gray-600 mt-0.5 truncate">{event.title}</p>
                            )}
                            {/* Línea 3: Metadata — duración, delegación, responsable */}
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                              {event.duration && event.duration > 0 && (
                                <span>{event.duration} min</span>
                              )}
                              {event.delegationName && (
                                <span>{event.delegationName}</span>
                              )}
                              {event.userName && (
                                <span className="text-gray-600">Resp. {event.userName}</span>
                              )}
                            </div>
                          </div>
                          {/* Avatar del responsable — siempre visible si hay userId */}
                          {event.userId && (
                            <span
                              className={`
                                inline-flex items-center justify-center flex-shrink-0
                                w-8 h-8 rounded-full text-[11px] font-bold text-white
                                ${getAvatarColor(event.userId)}
                              `}
                              title={event.userName || 'Responsable'}
                            >
                              {getEventAvatarInitials(event)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          /* ── Vista Semana / Mes (grid) ── */
          <>
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold text-gray-600 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {daysToShow.map((day) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, periodStart);
                const isTodayDate = isToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => onDateClick?.(day)}
                    className={`
                      p-2 border rounded-lg cursor-pointer transition-all duration-300
                      ${zoomed ? 'min-h-48' : 'min-h-24'}
                      ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${isTodayDate ? 'border-blue-500 border-2' : 'border-gray-200'}
                      hover:bg-blue-50
                    `}
                  >
                    <div
                      className={`
                        text-sm font-semibold mb-1
                        ${isTodayDate ? 'text-blue-600' : 'text-gray-900'}
                        ${!isCurrentMonth && 'text-gray-400'}
                      `}
                    >
                      {format(day, 'd')}
                    </div>

                    <div className="space-y-1">
                      {dayEvents.slice(0, zoomed ? 8 : 3).map((event) => (
                        <div
                          key={event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick?.(event);
                          }}
                          className={`
                            flex items-center gap-1 text-xs p-1 rounded text-white cursor-pointer
                            ${getEventColor(event)}
                            hover:opacity-80
                          `}
                          title={getGridTooltip(event)}
                        >
                          {event.type === 'appointment' && event.metadata?.modality && (
                            <span className="flex-shrink-0 opacity-90">
                              {(() => {
                                const ModalityIcon = getModalityIcon(event.metadata.modality);
                                return <ModalityIcon className="w-3.5 h-3.5" aria-hidden />;
                              })()}
                            </span>
                          )}
                          {event.userId && (
                            <span
                              className={`
                                inline-flex items-center justify-center flex-shrink-0
                                w-4 h-4 rounded-full text-[8px] font-bold text-white
                                ${getAvatarColor(event.userId)}
                                ring-1 ring-white/40
                              `}
                            >
                              {getEventAvatarInitials(event)}
                            </span>
                          )}
                          <span className="truncate">{getGridLabel(event)}</span>
                        </div>
                      ))}
                      {dayEvents.length > (zoomed ? 8 : 3) && (
                        <div className="text-xs text-gray-500 pl-1">
                          +{dayEvents.length - (zoomed ? 8 : 3)} más
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center justify-center gap-4 p-4 border-t border-gray-200 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded"></div>
          <span>Citas</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-600 rounded"></div>
          <span>Llamadas salientes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-teal-500 rounded"></div>
          <span>Llamadas entrantes</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-500 rounded"></div>
          <span>Tareas</span>
        </div>
      </div>
    </div>
  );
};
